import * as qsLib from 'qs';
import { isPagerDutyWebhookForCurrentEnv, useGenericWebhooks, webhooksUrlPrefix } from 'src/services/webhooks/webhooksEnv';

import integration from 'src/lib/api/integration';
import jira from 'src/lib/api/jira';
import pagerduty from 'src/lib/api/pagerduty';

const deletePdWebhook = async (task) => {
  const { webhook } = task;
  //Delete by ID
  if (webhook.id) {
    await pagerduty.deleteWebhook(webhook.id);
    //Delete by Name
    //check JIRA_integration_ as extra protection measure, we don't want delete webhooks that dont belong to Jira Integration
  } else if (webhook.name && webhook.name.startsWith('JIRA_integration_')) {
    const { webhooks } = await pagerduty.getWebhooks(webhook.name);
    await Promise.all(
      webhooks
        .filter(isPagerDutyWebhookForCurrentEnv)
        .map(w => pagerduty.deleteWebhook(w.id))
    );
  } else {
    throw new Error('Invalid webhook delete task');
  }
};

const createPdWebhook = async (task) => {
  const { webhookName, webhookConfig, service } = task;
  const { token } = await integration.signWebhook({ service_id: service.id, type: 'pagerduty' });
  if (useGenericWebhooks) {
    //prevent accidental creation of webhook to localhost.
    if (
      webhooksUrlPrefix.includes('127.0.0.1')
      || webhooksUrlPrefix.includes('localhost')
      || webhooksUrlPrefix.includes('lvh.me')
    ) {
      throw new Error(`Can not create webhook to ${webhooksUrlPrefix}. Use ngrok or other tool to proxy internet request to local host`);
    }

    return pagerduty.createGenericWebhook(webhookName, `${webhooksUrlPrefix}/webhooks/pagerduty?signature=${token}`, service.id);
  } else {
    return pagerduty.createJiraWebhook(webhookName, service.id, { ...webhookConfig, signature: token });
  }
};

const deleteJiraWebhook = async (task) => {
  const { webhook } = task;
  await jira.removeWebhook(webhook.id);
};

const createJiraWebhook = async ({ events, jql, queryParams, name }) => {
  const { token } = await integration.signWebhook({ jql, events, type: 'jira' });

  await jira.createWebhook({
    name,
    events,
    jql,
    url: `${webhooksUrlPrefix}/webhooks/jira?${qsLib.stringify({ ...queryParams, signature: token })}`
  });
};


async function createWebhookSubscription({ webhookName, service }) {
  const { token } = await integration.signWebhook({ service_id: service.id, type: 'PagerDutyV3' });
  const url = `${webhooksUrlPrefix}/webhooks/pagerduty?signature=${token}&dedupKey=${webhookName}`;
  await pagerduty.createWebhookSubscription({
    type: 'webhook_subscription',
    resource_type: 'incident',
    delivery_method: {
      type: 'http_delivery_method',
      url,
    },
    events: ['incident.priority_updated'],
    filter: { type: 'service_reference', id: service.id }
  });
}

async function deleteWebhookSubscription({ service, subscription: { id } = {} }) {
  if (!id && service.id) {
    let subscriptions = await pagerduty.getAllWebhookSubscriptions()
      .then(all => all.filter(w => w.delivery_method.url.includes('JIRA_integration_')));

    const s = subscriptions.find(s => s.filter.id === service.id);
    if (s) id = s.id;
  }
  if (id) {
    await pagerduty.deleteWebhookSubscription(id);
  }
}


export const webhooksTasksRunner = async ({ type, action, ...task }) => {
  if (type === 'pd' && action === 'delete') return deletePdWebhook(task);
  if (type === 'pd' && action === 'create') return createPdWebhook(task);
  if (type === 'subscription' && action === 'delete') return deleteWebhookSubscription(task);
  if (type === 'subscription' && action === 'create') return createWebhookSubscription(task);
  if (type === 'jira' && action === 'delete') return deleteJiraWebhook(task);
  if (type === 'jira' && action === 'create') return createJiraWebhook(task);
  throw new Error(`can not recognize action, most likely it's a programming error: ${JSON.stringify({ type, action })}`);
};
